import axios from 'axios';
import i18n from 'i18next'; // для работы с Accept-Language, если нужно
import Cookies from 'js-cookie';
import { refreshAccessToken } from './authApi';
import { clearAuthCookies, setAuthCookies } from '../libs/authCookies';


const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // установи базовый URL, если все запросы идут с одного домена
  withCredentials: true, // чтобы автоматически отправлялись куки
});

const REFRESH_URL = '/users/refresh-token';
const AUTH_PATH = '/authorization';

// Страницы без авторизации (вне RedirectAuthorization в AppRouter):
// 401 на них — норма для гостя, уводить на авторизацию нельзя
const PUBLIC_PATHS = [AUTH_PATH, '/sign-in', '/registration', '/qr-marks', '/error'];

const isPublicPage = () =>
  PUBLIC_PATHS.some(
    (path) =>
      window.location.pathname === path ||
      window.location.pathname.startsWith(`${path}/`),
  );

// Рефреш, который сейчас в процессе. Общий для интерсептора и RedirectAuthorization,
// чтобы один и тот же refresh_token не отправлялся дважды (при ротации второй запрос упадёт)
let refreshPromise: Promise<void> | null = null;

export const refreshSession = (): Promise<void> => {
  if (!refreshPromise) {
    const refresh_token = Cookies.get("refresh_token");
    if (!refresh_token) {
      return Promise.reject(new Error("refresh_token not found"));
    }

    refreshPromise = refreshAccessToken(refresh_token)
      .then((res) => {
        // Доменная копия со старым токеном иначе может перекрыть новую при Cookies.get
        Cookies.remove("refresh_token", { path: "/", domain: ".edge-apps.com" });
        setAuthCookies(res.data);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Сессия не восстанавливается — чистим куки и выкидываем на авторизацию.
// Полная перезагрузка заодно сбрасывает redux-стейт.
const redirectToAuthorization = () => {
  clearAuthCookies();
  if (!isPublicPage()) {
    window.location.replace(AUTH_PATH);
  }
};

// Интерсептор для обработки ответов и обновления токена
apiClient.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;

    // Сетевая ошибка / CORS — response нет, рефрешить нечего
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // 401 на сам рефреш обрабатывает тот, кто его вызвал
    if (originalRequest.url?.includes(REFRESH_URL)) {
      return Promise.reject(error);
    }

    // Повтор после рефреша снова получил 401 — токен не принимается
    if (originalRequest._retry) {
      redirectToAuthorization();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      // Параллельные 401 ждут один и тот же рефреш
      await refreshSession();
    } catch (err) {
      redirectToAuthorization();
      return Promise.reject(err);
    }

    return apiClient(originalRequest);
  }
);

// Интерсептор для каждого запроса, если нужно установить языковые настройки
apiClient.interceptors.request.use(
  (config) => {
    // Устанавливаем Accept-Language для всех запросов
    config.headers['Accept-Language'] = i18n.language?.includes("ru") ? "ru-RU" : "kk-KZ";
    return config;
  },
  (error) => Promise.reject(error)
);

export default apiClient;
